import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ArrowRightLeft, Moon, Sun, Menu, X, ChevronDown, Fuel, Calculator, MapPin, ChevronRight } from 'lucide-react';
import { cityDropdownLinks } from '../../data/fuelPricePages';

/* ─── FAQ data ─────────────────────────────────────────────────────────── */
const FAQ_ITEMS = [
  {
    q: 'How do I convert MPG to kilometers per liter?',
    a: 'Converting fuel consumption from miles per gallon (MPG) to kilometers per liter is simpler than you think. Just multiply the MPG value by 0.425. For example, if a car in the US has 25 MPG fuel economy, after conversion you get: 25 × 0.425 = 10.625 km/L. The higher the MPG, the more fuel-efficient the vehicle - just like with km/L. Our calculator does this automatically so you can instantly compare the efficiency of different car models available at US rental agencies.',
  },
  {
    q: 'How do I convert MPG to liters per 100 km?',
    a: 'Converting from miles per gallon (MPG) to liters per 100 km - the European standard - requires one simple calculation: divide 235 by the MPG value. Formula: L/100km = 235 ÷ MPG. For example, a car with 30 MPG gives: 235 ÷ 30 = 7.83 L/100km. Note that unlike MPG, a lower L/100km value means a more efficient car. A car doing 35 MPG (6.7 L/100km) is more economical than one doing 25 MPG (9.4 L/100km).',
  },
  {
    q: 'What are the most common mistakes when converting gallons to liters?',
    a: 'The most common mistake is using the wrong conversion factor. Many tourists mistakenly assume a gallon is "about 4 liters", which leads to underestimating fuel by over 5%. Another frequent error is confusing the US gallon with the Imperial gallon (UK) - the difference is almost 20%! Some travelers also apply the conversion backwards (dividing liters by 3.78 instead of multiplying), which completely distorts results. Our calculator eliminates all these potential errors, ensuring accurate conversions every time.',
  },
  {
    q: 'Is fuel cheaper in the USA than in Europe?',
    a: 'Comparing US and European fuel prices often leads to wrong conclusions due to unit and currency differences. At first glance, $3.50 per gallon may seem low, but after conversion that is roughly 0.92 USD per liter - which is comparable to many European countries after accounting for exchange rates. The actual price difference depends on the current exchange rate and the US region - fuel prices vary significantly between states due to different state taxes. California is known for high fuel prices, while Texas and southern states tend to be cheaper.',
  },
  {
    q: 'How do I plan a fuel budget for a US road trip?',
    a: 'Planning a fuel budget for a US road trip requires a few key factors. First, determine your route and total miles using Google Maps. Then check the rental car\'s MPG rating (available in the vehicle specs). Divide total distance by MPG to get gallons needed. For example: 2000 miles ÷ 25 MPG = 80 gallons. Check average fuel prices in the states you\'ll pass through at GasBuddy.com. Multiply gallons by the average price to get your total cost. Add a 10-15% buffer for unexpected detours, traffic, or difficult driving conditions. Our calculator automates all these calculations for precise trip budgeting.',
  },
];

/* ─── Component ────────────────────────────────────────────────────────── */
const FuelPortalEN = () => {
  const [darkMode, setDarkMode]               = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen]   = useState(false);
  const [pricesOpen, setPricesOpen]           = useState(false);
  const [mobilePricesOpen, setMobilePricesOpen] = useState(false);
  const dropdownRef = useRef(null);

  const [dollarRate, setDollarRate]           = useState(4.12);
  const [fuelPrice]                           = useState(3.50);
  const [fuelPricePLN, setFuelPricePLN]       = useState(6.50);
  const [amount, setAmount]                   = useState('');
  const [conversionDirection, setConversionDirection] = useState('toZloty');
  const [calculatedResult, setCalculatedResult] = useState(null);
  const [isLoading, setIsLoading]             = useState(false);
  const [activeTab, setActiveTab]             = useState('consumption');
  const [swapRotation, setSwapRotation]       = useState(0);

  const [mpg, setMpg]                         = useState('30');
  const [distance, setDistance]               = useState('200');
  const [distanceUnit, setDistanceUnit]       = useState('km');
  const [consumptionResult, setConsumptionResult] = useState(null);

  const [openFaq, setOpenFaq]                 = useState(null);

  /* fetch exchange rate */
  useEffect(() => {
    const fetchExchangeRate = async () => {
      setIsLoading(true);
      try {
        const response = await fetch('https://api.nbp.pl/api/exchangerates/rates/a/usd/');
        if (!response.ok) throw new Error('rate fetch failed');
        const data = await response.json();
        const rate = data.rates[0].mid;
        setDollarRate(rate);
        setFuelPricePLN(fuelPrice * rate / 3.785);
      } catch {
        setDollarRate(4.12);
      } finally {
        setIsLoading(false);
      }
    };
    fetchExchangeRate();
  }, [fuelPrice]);

  /* close dropdown on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setPricesOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* calculator */
  const calculatePrice = useCallback((value) => {
    const n = parseFloat(value);
    if (isNaN(n) || n <= 0) { setCalculatedResult(null); return; }

    if (conversionDirection === 'toZloty') {
      const totalUSD   = n * fuelPrice;
      const totalPLN   = totalUSD * dollarRate;
      const liters     = n * 3.785;
      setCalculatedResult({
        unitPrice: (totalPLN / liters).toFixed(2),
        total: totalPLN.toFixed(2),
        convertedAmount: liters.toFixed(2),
        unit: 'liters', currency: 'PLN',
      });
    } else {
      const gallons    = n / 3.785;
      const totalPLN   = n * fuelPricePLN;
      const totalUSD   = totalPLN / dollarRate;
      setCalculatedResult({
        unitPrice: (totalUSD / gallons).toFixed(2),
        total: totalUSD.toFixed(2),
        convertedAmount: gallons.toFixed(2),
        unit: 'gallons', currency: 'USD',
      });
    }
  }, [conversionDirection, fuelPrice, dollarRate, fuelPricePLN]);

  const handleAmountChange = (e) => {
    setAmount(e.target.value);
    calculatePrice(e.target.value);
  };

  const handleSwap = () => {
    setSwapRotation(r => r + 180);
    setConversionDirection(d => d === 'toZloty' ? 'toDollar' : 'toZloty');
    setAmount('');
    setCalculatedResult(null);
  };

  /* MPG calculator */
  const handleDistanceUnitChange = (unit) => {
    if (unit === distanceUnit) return;
    const val = parseFloat(distance);
    if (!isNaN(val) && val > 0) {
      setDistance(unit === 'km'
        ? (val * 1.60934).toFixed(0)
        : (val / 1.60934).toFixed(0)
      );
    }
    setDistanceUnit(unit);
  };

  const calculateConsumption = useCallback(() => {
    const mpgV = parseFloat(mpg);
    const distV = parseFloat(distance);
    if (!mpgV || !distV || mpgV <= 0 || distV <= 0) { setConsumptionResult(null); return; }

    const distMiles = distanceUnit === 'km' ? distV / 1.60934 : distV;
    const distKm    = distanceUnit === 'km' ? distV : distV * 1.60934;

    const litersPer100km  = (235.21 / mpgV).toFixed(2);
    const gallonsUsed     = distMiles / mpgV;
    const litersUsed      = (gallonsUsed * 3.785).toFixed(2);
    const costUSD         = (gallonsUsed * fuelPrice).toFixed(2);
    const costPLN         = (parseFloat(costUSD) * dollarRate).toFixed(2);

    setConsumptionResult({
      mpg: mpgV, litersPer100km,
      gallonsUsed: gallonsUsed.toFixed(2), litersUsed,
      distanceMiles: distMiles.toFixed(1),
      distanceKm: distKm.toFixed(1),
      costUSD, costPLN,
    });
  }, [mpg, distance, distanceUnit, fuelPrice, dollarRate]);

  useEffect(() => {
    if (mpg && distance && parseFloat(mpg) > 0 && parseFloat(distance) > 0) {
      calculateConsumption();
    } else {
      setConsumptionResult(null);
    }
  }, [mpg, distance, distanceUnit, calculateConsumption]);

  /* ─── design tokens ──────────────────────────────────────────────────── */
  const dm = darkMode;

  const bg          = dm ? 'bg-[#09090B]'   : 'bg-white';
  const bgAlt       = dm ? 'bg-[#111118]'   : 'bg-[#F9FAFB]';
  const cardBg      = dm ? 'bg-[#18181B]'   : 'bg-white';
  const border      = dm ? 'border-[#27272A]' : 'border-[#5B5B5B]';
  const textPrimary = dm ? 'text-[#F9FAFB]' : 'text-[#111827]';
  const textSecond  = dm ? 'text-[#71717A]' : 'text-[#2E3137]';
  const navBg       = dm ? 'bg-[#09090B]/95 border-[#27272A]' : 'bg-white/95 border-[#5B5B5B]';
  const inputCls    = `w-full h-14 px-4 rounded-xl border text-base outline-none transition-all duration-200 ${
    dm
      ? 'bg-[#111118] border-[#27272A] text-white placeholder-[#52525B] focus:border-accent'
      : 'bg-white border-[#5B5B5B] text-[#111827] placeholder-[#9CA3AF] focus:border-accent'
  } focus:ring-2 focus:ring-accent/20`;
  const labelCls  = `block text-sm font-medium mb-1.5 ${textSecond}`;

  return (
    <>
      <Helmet>
        <title>US Fuel Price Calculator - Gallons & Liters Converter</title>
        <meta name="description" content="Calculate US fuel prices, convert between gallons and liters, USD and PLN. Free fuel cost calculator for road trips in America." />
        <link rel="canonical" href="https://galonyilitry.pl/en" />
        <html lang="en" />
      </Helmet>

      <div className={`min-h-screen ${bg} font-sans transition-colors duration-300`}>

        {/* ─── NAV ─────────────────────────────────────────────────────── */}
        <header className={`${navBg} border-b fixed top-0 w-full z-20 backdrop-blur-md`}>
          <div className="max-w-content mx-auto px-6 h-14 flex justify-between items-center">

            <Link to="/en" className="font-bold text-lg tracking-tight text-accent">
              Gallons&amp;Liters
            </Link>

            {/* desktop nav */}
            <nav className="hidden md:flex items-center gap-7">
              {[
                { to: '/en/gallons-to-liters-converter', label: 'Gallons → Liters' },
                { to: '/en/liters-to-gallons-converter', label: 'Liters → Gallons' },
              ].map(({ to, label }) => (
                <Link key={to} to={to}
                  className={`text-sm font-medium ${textSecond} hover:${dm ? 'text-white' : 'text-[#111827]'} transition-colors underline-offset-4 hover:underline`}>
                  {label}
                </Link>
              ))}

              {/* prices dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setPricesOpen(o => !o)}
                  className={`flex items-center gap-1 text-sm font-medium ${textSecond} hover:${dm ? 'text-white' : 'text-[#111827]'} transition-colors`}
                >
                  Fuel Prices
                  <ChevronDown size={13} className={`transition-transform duration-200 ${pricesOpen ? 'rotate-180' : ''}`} />
                </button>
                {pricesOpen && (
                  <div className={`absolute top-full left-0 mt-2 w-52 rounded-2xl border shadow-lg py-1.5 z-50 ${cardBg} ${border} animate-fade-in`}>
                    {cityDropdownLinks.map(({ label, path }) => (
                      <Link key={path} to={path}
                        onClick={() => setPricesOpen(false)}
                        className={`block px-4 py-2 text-sm ${textSecond} hover:text-accent transition-colors`}>
                        {label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link to="/en/blog"
                className={`text-sm font-medium ${textSecond} hover:${dm ? 'text-white' : 'text-[#111827]'} transition-colors underline-offset-4 hover:underline`}>
                Blog
              </Link>
            </nav>

            {/* right controls */}
            <div className="flex items-center gap-2">
              <button onClick={() => setMobileMenuOpen(o => !o)}
                className={`md:hidden p-1.5 rounded-lg ${textSecond} hover:text-accent transition-colors`}>
                {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
              <Link to="/"
                className={`text-sm font-medium px-2.5 py-1 rounded-lg border ${border} ${textSecond} hover:text-accent hover:border-accent transition-colors`}>
                PL
              </Link>
              <button onClick={() => setDarkMode(d => !d)}
                className={`p-1.5 rounded-lg ${textSecond} hover:text-accent transition-colors`}>
                {dm ? <Sun size={16} /> : <Moon size={16} />}
              </button>
            </div>
          </div>

          {/* mobile menu */}
          {mobileMenuOpen && (
            <div className={`md:hidden border-t ${border} ${dm ? 'bg-[#09090B]' : 'bg-white'}`}>
              <div className="max-w-content mx-auto px-6 flex flex-col py-2">
                {[
                  { to: '/en/gallons-to-liters-converter', label: 'Gallons → Liters' },
                  { to: '/en/liters-to-gallons-converter', label: 'Liters → Gallons' },
                ].map(({ to, label }) => (
                  <Link key={to} to={to} onClick={() => setMobileMenuOpen(false)}
                    className={`text-sm font-medium py-3 border-b ${border} ${textSecond} text-center`}>
                    {label}
                  </Link>
                ))}
                <button onClick={() => setMobilePricesOpen(o => !o)}
                  className={`text-sm font-medium py-3 border-b ${border} ${textSecond} flex items-center justify-center gap-1.5`}>
                  Fuel Prices
                  <ChevronDown size={13} className={`transition-transform ${mobilePricesOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobilePricesOpen && cityDropdownLinks.map(({ label, path }) => (
                  <Link key={path} to={path}
                    onClick={() => { setMobileMenuOpen(false); setMobilePricesOpen(false); }}
                    className={`text-sm py-2.5 border-b ${border} ${textSecond} text-center`}>
                    {label}
                  </Link>
                ))}
                <Link to="/en/blog" onClick={() => setMobileMenuOpen(false)}
                  className={`text-sm font-medium py-3 ${textSecond} text-center`}>
                  Blog
                </Link>
              </div>
            </div>
          )}
        </header>

        {/* ─── HERO ─────────────────────────────────────────────────────── */}
        <main>
          <section id="kalkulator" className="pt-28 pb-16 px-4">
            <div className="max-w-content mx-auto">

              {/* headline */}
              <div className="text-center mb-10 max-w-2xl mx-auto">
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-5 ${dm ? 'bg-accent/15 text-accent-dark' : 'bg-accent-light text-accent'}`}>
                  <Fuel size={11} /> Live NBP Exchange Rate
                </span>
                <h1 className={`text-4xl md:text-[3.25rem] font-bold leading-tight tracking-tight mb-4 ${textPrimary}`}>
                  How much does gas cost<br className="hidden md:block" /> in the USA?
                </h1>
                <p className={`text-base md:text-lg ${textSecond} leading-relaxed`}>
                  Convert US fuel prices from dollars to PLN. The calculator uses the live dollar exchange rate and converts gallons to liters.
                </p>
              </div>

              {/* rate strip */}
              <div className={`max-w-2xl mx-auto flex items-center justify-center gap-6 mb-6 text-sm ${textSecond}`}>
                <span className="flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${isLoading ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'}`} />
                  {isLoading ? 'Fetching rate...' : `USD rate: ${dollarRate.toFixed(4)} PLN`}
                </span>
                <span className={`w-px h-4 ${dm ? 'bg-[#27272A]' : 'bg-[#E5E7EB]'}`} />
                <span>US Gas: ${fuelPrice.toFixed(2)}/gal</span>
              </div>

              {/* ─── CALCULATOR CARD ─────────────────────────────────────── */}
              <div className={`max-w-2xl mx-auto rounded-2xl border ${border} ${cardBg}`}>

                {/* tab switcher */}
                <div className={`p-4 border-b ${border} flex gap-2`}>
                  {[
                    { id: 'consumption', icon: <Calculator size={14} />,     label: 'MPG Calculator' },
                    { id: 'converter',   icon: <ArrowRightLeft size={14} />, label: 'Price Converter' },
                  ].map(({ id, icon, label }) => (
                    <button key={id} onClick={() => setActiveTab(id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 border-2 border-[#22e321] ${
                        activeTab === id
                          ? 'bg-accent text-white shadow-sm'
                          : `${textSecond} hover:text-accent ${dm ? 'hover:bg-white/5' : 'hover:bg-[#F3F4F6]'}`
                      }`}>
                      {icon}{label}
                    </button>
                  ))}
                </div>

                <div className="p-6">

                  {/* ── CONVERTER TAB ───────────────────────────────────── */}
                  {activeTab === 'converter' && (
                    <div>
                      {/* direction labels */}
                      <div className="flex items-center gap-3 mb-6">
                        <div className={`flex-1 text-center py-2.5 rounded-xl text-sm font-medium border ${border} ${dm ? 'text-[#A1A1AA]' : 'text-[#6B7280]'}`}>
                          {conversionDirection === 'toZloty' ? 'USD / Gallons' : 'PLN / Liters'}
                        </div>

                        {/* swap */}
                        <button
                          onClick={handleSwap}
                          style={{ transform: `rotate(${swapRotation}deg)`, transition: 'transform 0.4s cubic-bezier(.4,0,.2,1)' }}
                          className="w-10 h-10 flex-shrink-0 rounded-xl bg-accent text-white flex items-center justify-center shadow-sm hover:bg-accent-hover active:scale-95 transition-colors"
                          aria-label="Swap direction">
                          <ArrowRightLeft size={16} />
                        </button>

                        <div className={`flex-1 text-center py-2.5 rounded-xl text-sm font-medium border ${border} ${dm ? 'text-[#A1A1AA]' : 'text-[#6B7280]'}`}>
                          {conversionDirection === 'toZloty' ? 'PLN / Liters' : 'USD / Gallons'}
                        </div>
                      </div>

                      {/* input */}
                      <div className="mb-5">
                        <label className={labelCls}>
                          {conversionDirection === 'toZloty' ? 'Number of gallons' : 'Number of liters'}
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="any"
                          placeholder={conversionDirection === 'toZloty' ? 'e.g. 10' : 'e.g. 50'}
                          value={amount}
                          onChange={handleAmountChange}
                          className={`${inputCls} input-green`}
                        />
                      </div>

                      {/* result */}
                      {calculatedResult ? (
                        <div key={calculatedResult.unitPrice} className={`rounded-xl p-5 border animate-fade-in-up ${dm ? 'bg-accent/10 border-accent/30' : 'bg-accent-light border-accent/20'}`}>
                          <div className={`text-2xl font-bold mb-1 text-accent`}>
                            {conversionDirection === 'toZloty'
                              ? `${calculatedResult.unitPrice} PLN / liter`
                              : `$${calculatedResult.unitPrice} / gallon`}
                          </div>
                          <p className={`text-sm ${textSecond}`}>
                            Total: <strong className={textPrimary}>{calculatedResult.total} {calculatedResult.currency === 'PLN' ? 'PLN' : '$'}</strong>
                            {' '}for <strong className={textPrimary}>{calculatedResult.convertedAmount} {calculatedResult.unit}</strong>
                          </p>
                        </div>
                      ) : (
                        <div className={`rounded-xl p-5 border border-dashed ${border} text-center ${textSecond} text-sm`}>
                          Enter an amount to see the result
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── MPG TAB ─────────────────────────────────────────── */}
                  {activeTab === 'consumption' && (
                    <div>
                      <p className={`text-sm ${textSecond} mb-5`}>
                        Convert MPG to L/100km and calculate your US road trip fuel cost.
                      </p>
                      <div className="grid grid-cols-2 gap-4 mb-5">
                        <div>
                          <label className={labelCls}>Consumption (MPG)</label>
                          <input type="number" min="0" placeholder="e.g. 25"
                            value={mpg} onChange={e => setMpg(e.target.value)}
                            className={`${inputCls} input-green`} />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1.5">
                            <span className={`text-sm font-medium ${textSecond}`}>
                              Distance ({distanceUnit})
                            </span>
                            <div className={`flex rounded-lg border ${border} overflow-hidden text-xs font-medium`}>
                              {['km', 'miles'].map(unit => (
                                <button key={unit}
                                  onClick={() => handleDistanceUnitChange(unit)}
                                  className={`px-2.5 py-1 transition-colors ${
                                    distanceUnit === unit
                                      ? 'bg-accent text-white'
                                      : `${textSecond} ${dm ? 'hover:bg-white/10' : 'hover:bg-[#F3F4F6]'}`
                                  }`}>
                                  {unit}
                                </button>
                              ))}
                            </div>
                          </div>
                          <input type="number" min="0"
                            placeholder={distanceUnit === 'km' ? 'e.g. 200' : 'e.g. 125'}
                            value={distance} onChange={e => setDistance(e.target.value)}
                            className={`${inputCls} input-green`} />
                        </div>
                      </div>

                      {consumptionResult && (
                        <div key={consumptionResult.costPLN} className="animate-fade-in-up space-y-3">
                          {/* hero result */}
                          <div className={`rounded-xl p-5 border ${dm ? 'bg-accent/10 border-accent/30' : 'bg-accent-light border-accent/20'}`}>
                            <div className="text-2xl font-bold text-accent mb-0.5">
                              ${consumptionResult.costUSD}
                            </div>
                            <p className={`text-sm ${textSecond}`}>
                              Fuel cost for {consumptionResult.distanceKm} km ({consumptionResult.distanceMiles} miles)
                            </p>
                          </div>

                          {/* details grid */}
                          <div className={`grid grid-cols-2 gap-3 text-sm`}>
                            {[
                              { label: 'Consumption', value: `${consumptionResult.litersPer100km} L/100km` },
                              { label: 'Fuel used', value: `${consumptionResult.litersUsed} L (${consumptionResult.gallonsUsed} gal)` },
                              { label: 'Cost in USD', value: `$${consumptionResult.costUSD}` },
                              { label: 'Cost in PLN', value: `${consumptionResult.costPLN} PLN` },
                            ].map(({ label, value }) => (
                              <div key={label} className={`rounded-xl p-3.5 border ${border} ${cardBg}`}>
                                <div className={`text-xs ${textSecond} mb-0.5`}>{label}</div>
                                <div className={`font-semibold ${textPrimary}`}>{value}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                </div>
              </div>
              {/* end calculator card */}

            </div>
          </section>

          {/* ─── FUEL PRICE TILES ──────────────────────────────────────── */}
          <section className={`${bgAlt} py-14 px-4`}>
            <div className="max-w-content mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-xl font-bold ${textPrimary}`}>
                  Current US fuel prices
                </h2>
                <Link to="/aktualne-ceny-paliw-w-usa"
                  className={`text-sm font-medium text-accent flex items-center gap-1 hover:gap-2 transition-all`}>
                  All states <ChevronRight size={14} />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2.5">
                {cityDropdownLinks.map(({ label, path }) => (
                  <Link key={path} to={path}
                    className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border ${border} ${cardBg} hover:border-accent group transition-all duration-200`}>
                    <MapPin size={13} className={`${textSecond} group-hover:text-accent transition-colors flex-shrink-0`} />
                    <span className={`text-sm font-medium ${textPrimary} group-hover:text-accent transition-colors`}>{label}</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* ─── TOOLS / FEATURES ──────────────────────────────────────── */}
          <section className="py-16 px-4">
            <div className="max-w-content mx-auto">
              <h2 className={`text-xl font-bold mb-6 ${textPrimary}`}>Calculators & converters</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Link to="/en/gallons-to-liters-converter"
                  className={`md:col-span-2 group flex flex-col justify-between p-6 rounded-2xl border ${border} ${cardBg} hover:border-accent transition-all duration-200 min-h-[140px]`}>
                  <div>
                    <ArrowRightLeft size={20} className="text-accent mb-3" />
                    <h3 className={`font-semibold text-base mb-1 ${textPrimary}`}>Gallons to Liters Converter</h3>
                    <p className={`text-sm ${textSecond}`}>Find out how many liters are in 1 gallon and calculate fuel cost in PLN</p>
                  </div>
                  <span className="text-accent text-sm font-medium mt-4 flex items-center gap-1 group-hover:gap-2 transition-all">
                    Convert <ChevronRight size={14} />
                  </span>
                </Link>

                <Link to="/en/liters-to-gallons-converter"
                  className={`group flex flex-col justify-between p-6 rounded-2xl border ${border} ${cardBg} hover:border-accent transition-all duration-200`}>
                  <div>
                    <ArrowRightLeft size={20} className="text-accent mb-3 rotate-90" />
                    <h3 className={`font-semibold text-base mb-1 ${textPrimary}`}>Liters to Gallons</h3>
                    <p className={`text-sm ${textSecond}`}>Convert liters to US gallons</p>
                  </div>
                  <span className="text-accent text-sm font-medium mt-4 flex items-center gap-1 group-hover:gap-2 transition-all">
                    Convert <ChevronRight size={14} />
                  </span>
                </Link>

                <Link to="/en#kalkulator"
                  className={`group flex flex-col justify-between p-6 rounded-2xl border ${border} ${cardBg} hover:border-accent transition-all duration-200`}>
                  <div>
                    <Calculator size={20} className="text-accent mb-3" />
                    <h3 className={`font-semibold text-base mb-1 ${textPrimary}`}>MPG Calculator</h3>
                    <p className={`text-sm ${textSecond}`}>Convert MPG to L/100km and calculate trip cost</p>
                  </div>
                  <span className="text-accent text-sm font-medium mt-4 flex items-center gap-1 group-hover:gap-2 transition-all">
                    Calculate <ChevronRight size={14} />
                  </span>
                </Link>

                <Link to="/en/blog"
                  className={`md:col-span-2 group flex items-center justify-between p-6 rounded-2xl border ${border} ${dm ? 'bg-accent/10 border-accent/30' : 'bg-accent-light border-accent/20'} hover:border-accent transition-all duration-200`}>
                  <div>
                    <h3 className={`font-semibold text-base mb-1 text-accent`}>Blog - US travel tips</h3>
                    <p className={`text-sm ${textSecond}`}>How to refuel, state fuel prices, US car imports and more</p>
                  </div>
                  <ChevronRight size={18} className="text-accent flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </section>

          {/* ─── HOW TO CONVERT GALLONS TO LITERS ──────────────────────── */}
          <section className="py-14 px-4">
            <div className="max-w-content mx-auto max-w-3xl">
              <h2 className={`text-xl font-bold mb-4 ${textPrimary}`}>
                How to convert gallons to liters?
              </h2>
              <p className={`text-sm leading-relaxed ${textSecond}`}>
                Converting US gallons to liters is a basic skill when traveling in America. One US gallon equals exactly
                3.78541 liters. For quick mental calculations you can use the approximate value of 3.8 liters. So when
                you see a 15-gallon tank at a gas station, that's about 57 liters (15 × 3.8 = 57). It's also worth
                knowing that the US gallon differs from the Imperial gallon (UK gallon) used in Great Britain.
                The Imperial gallon is about 4.55 liters, so it's larger than the US gallon. In the USA the US gallon
                is always used, so you don't need to worry about this difference when traveling in the States.
                Our calculator always uses the correct US gallon conversion factor for accurate results.
              </p>
            </div>
          </section>

          {/* ─── FAQ ───────────────────────────────────────────────────── */}
          <section className={`${bgAlt} py-16 px-4`}>
            <div className="max-w-content mx-auto">
              <h2 className={`text-xl font-bold mb-8 ${textPrimary}`}>
                Frequently asked questions
              </h2>

              <div className={`rounded-2xl border ${border} overflow-hidden ${cardBg}`}>
                {FAQ_ITEMS.map((item, idx) => {
                  const open = openFaq === idx;
                  return (
                    <div key={idx} className={`border-b last:border-b-0 ${border}`}>
                      <button
                        onClick={() => setOpenFaq(open ? null : idx)}
                        className={`w-full flex items-center justify-between px-4 py-4 text-left transition-colors ${dm ? 'hover:bg-white/5' : 'hover:bg-[#F9FAFB]'}`}
                      >
                        <span className={`text-sm font-semibold pr-4 ${textPrimary}`}>{item.q}</span>
                        <ChevronDown size={16} className={`flex-shrink-0 text-accent transition-transform duration-300 ${open ? 'rotate-180' : ''}`} />
                      </button>
                      {open && (
                        <div className={`px-4 pb-5 text-sm leading-relaxed text-left ${textSecond} animate-fade-in-up`}>
                          {item.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

        </main>

        {/* ─── FOOTER ────────────────────────────────────────────────── */}
        <footer className={`${dm ? 'bg-[#18181B] border-[#27272A]' : 'bg-[#374151] border-[#4B5563]'} border-t py-8 px-4`}>
          <div className="max-w-content mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <span className="font-bold text-sm text-accent">Gallons&amp;Liters</span>
            <p className="text-xs text-[#D1D5DB]">
              © 2026 GalonyiLitry.pl · All calculators are for informational purposes only
            </p>
            <div className="flex flex-col md:flex-row items-center gap-3 text-xs text-[#9CA3AF]">
              <Link to="/" className="hover:text-accent transition-colors">
                Polski →
              </Link>
              <span className="hidden md:inline text-[#6B7280]">·</span>
              <span>
                Design &amp; code:{' '}
                <a href="https://crowbox.pl" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">Crowbox.pl</a>
                {' '}&amp;{' '}
                <a href="https://reklamaprawnika.pl" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors">Reklamaprawnika.pl</a>
              </span>
            </div>
          </div>
        </footer>

      </div>
    </>
  );
};

export default FuelPortalEN;
